import * as React from "react";
import "./PatientDashboard.scss";
import {
  PatientFormConfig,
  PatientInformationSection,
  PatientProfileWithConfiguration,
} from "../patient-profile/PatientProfile";
import ModalPopup from "../commons/components/modal/ModalPopup";
import PersonalInfoForm from "./PersonalInfoForm";
import DualInsuranceInfoForm from "./DualInsuranceInfoForm";
import DentalInfoForm from "./DentalInfoForm";
import HealthHistoryInfoForm from "./HealthHistoryInfoForm";
import AuthorizationInfoForm from "./AuthorizationInfoForm";
import HouseholdInfoForm from "./HouseholdInfoForm";
import { fillTemplateString } from "../commons/utils/text-rendering";
import Country from "../commons/model/address/Country";

export function PatientInformationModal({
  patientProfileWithConfiguration,
  section,
  availableCountries,
  closeModal,
  forMyself,
}: {
  patientProfileWithConfiguration: PatientProfileWithConfiguration;
  section: PatientInformationSection;
  availableCountries: Country[];
  closeModal: () => void;
  forMyself: boolean;
}) {
  const composeTitle = (selfTitle: string, otherTitleTemplate: string) => {
    if (forMyself) return selfTitle;
    else
      return fillTemplateString(otherTitleTemplate, {
        firstname: patientProfileWithConfiguration.patientProfile.personalInformation.firstName,
      });
  };

  if (!section) return null;
  const patientProfile = patientProfileWithConfiguration.patientProfile;
  const configuration = patientProfileWithConfiguration.configuration;
  switch (section) {
    case PatientInformationSection.PERSONAL_INFO:
      return (
        <ModalPopup closeModal={closeModal} id="modal-personal-information-form" fullscreen={true}>
          <PersonalInfoForm
            {...configuration.personalInfo}
            patientInformation={patientProfile.personalInformation}
            availableCountries={availableCountries}
            afterSubmit={() => window.location.reload()}
            title={composeTitle(
              i18n["Form.PersonalInfo.Title.ForMySelf"],
              i18n["Form.PersonalInfo.Title.ForOther"]
            )}
          />
        </ModalPopup>
      );
    case PatientInformationSection.INSURANCE_INFO:
      return (
        <ModalPopup closeModal={closeModal} id="modal-insurance-information-form" fullscreen={true}>
          <DualInsuranceInfoForm
            {...configuration.insuranceInfo}
            primaryInsuranceInfo={patientProfile.primaryInsuranceInformation}
            secondaryInsuranceInfo={patientProfile.secondaryInsuranceInformation}
            defaultCountry={patientProfile.personalInformation.country}
            availableCountries={availableCountries}
            afterSubmit={() => window.location.reload()}
            title={composeTitle(
              i18n["Form.InsuranceInfo.Title.ForMySelf"],
              i18n["Form.InsuranceInfo.Title.ForOther"]
            )}
          />
        </ModalPopup>
      );
    case PatientInformationSection.DENTAL_INFO:
      return (
        <ModalPopup closeModal={closeModal} id="modal-dental-information-form" fullscreen={true}>
          <DentalInfoForm
            {...configuration.dentalInfo}
            dentalInfo={patientProfile.dentalInformation}
            afterSubmit={() => window.location.reload()}
            title={composeTitle(
              i18n["Form.DentalInformation.Title.ForMySelf"],
              i18n["Form.DentalInformation.Title.ForOther"]
            )}
          />
        </ModalPopup>
      );
    case PatientInformationSection.HEALTH_HISTORY_INFO:
      return (
        <ModalPopup
          closeModal={closeModal}
          id="modal-health-history-information-form"
          fullscreen={true}
        >
          <HealthHistoryInfoForm
            {...configuration.healthHistoryInfo}
            healthHistoryInfo={patientProfile.healthHistoryInformation}
            afterSubmit={() => window.location.reload()}
            title={composeTitle(
              i18n["Form.HealthHistoryInformation.Title.ForMySelf"],
              i18n["Form.HealthHistoryInformation.Title.ForOther"]
            )}
          />
        </ModalPopup>
      );
    case PatientInformationSection.AUTHORIZATION_INFO:
      return (
        <ModalPopup closeModal={closeModal} id="modal-authorization-form" fullscreen={true}>
          <AuthorizationInfoForm
            {...configuration.authorizationInfo}
            authorizationInfo={patientProfile.authorizationInformation}
            afterSubmit={() => window.location.reload()}
            title={i18n["Form.Authorization.Title"]}
          />
        </ModalPopup>
      );
    case PatientInformationSection.HOUSEHOLD_INFO:
      return (
        <ModalPopup closeModal={closeModal} id="modal-household-information-form" fullscreen={true}>
          <HouseholdInfoForm
            {...configuration.householdInfo}
            householdInfo={patientProfile.householdInformation}
            afterSubmit={() => window.location.reload()}
            skippable={false}
            title={composeTitle(
              i18n["Form.Household.Title.ForMySelf"],
              i18n["Form.Household.Title.ForOther"]
            )}
          />
        </ModalPopup>
      );
  }
}

export function getAvailableSections(
  patientFormConfig: PatientFormConfig
): PatientInformationSection[] {
  return Object.values(PatientInformationSection).filter((section) =>
    patientFormConfig[section].fields.some((f) => f.available)
  );
}
